import { useMemo } from 'react';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import qs from 'query-string';
import { Endpoints } from 'UI/constants/endpoints';
import { SearchProjectItemTypeIds } from 'UI/constants/entityTypes';
import { UIStatus } from 'UI/constants/status';

const INITIAL_RESULTS_STATE = { total: 0 };
const COMPANY_ITEM_TYPE_ID = String(SearchProjectItemTypeIds.Company);

const getItemSearchProjectTypes = itemSearchProjectTypes => {
  if (Array.isArray(itemSearchProjectTypes)) return itemSearchProjectTypes.map(String);

  if (typeof itemSearchProjectTypes === 'string') {
    return itemSearchProjectTypes
      .split(',')
      .map(value => value.trim())
      .filter(Boolean);
  }

  return [];
};

const getCompaniesToExtractEndpoint = ({
  queryParams,
  searchProjectId,
  shouldExtractCompanyContacts
}) => {
  if (!shouldExtractCompanyContacts || !searchProjectId) return null;

  const parsedQueryParams = qs.parse(queryParams?.params || '');
  const itemSearchProjectTypes = getItemSearchProjectTypes(
    parsedQueryParams.itemSearchProjectTypes
  );
  const hasItemTypeFilter = itemSearchProjectTypes.length > 0;
  const includesCompanies = itemSearchProjectTypes.includes(COMPANY_ITEM_TYPE_ID);

  if (hasItemTypeFilter && !includesCompanies) return null;

  return qs.stringifyUrl(
    {
      url: Endpoints.SearchProjectsInventory.replace(':id', searchProjectId),
      query: {
        ...parsedQueryParams,
        itemSearchProjectTypes: COMPANY_ITEM_TYPE_ID,
        page: 1,
        perPage: 10
      }
    },
    { arrayFormat: 'comma' }
  );
};

export const useExtractableCompaniesCount = ({
  queryParams,
  searchProjectId,
  shouldExtractCompanyContacts
}) => {
  const queryParamsString = queryParams?.params;

  const endpoint = useMemo(
    () =>
      getCompaniesToExtractEndpoint({
        queryParams: queryParamsString ? { params: queryParamsString } : null,
        searchProjectId,
        shouldExtractCompanyContacts
      }),
    [queryParamsString, searchProjectId, shouldExtractCompanyContacts]
  );

  const { state } = useFetchWithStatusV2({
    endpoint,
    initialState: INITIAL_RESULTS_STATE,
    initialStatus: endpoint ? UIStatus.Loading : UIStatus.Success
  });
  const hasError = !!endpoint && state.status === UIStatus.Error;
  const totalResultsCount = Number(state.results?.total || 0);
  const count = hasError || !endpoint ? 0 : totalResultsCount;

  return {
    count,
    error: state.error,
    hasError,
    isLoading: endpoint ? state.status === UIStatus.Loading : false,
    state
  };
};
